import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon, ClockIcon, BikeIcon, BadgeCheckIcon, ArrowRightIcon } from 'lucide-react';
import { BrandStore } from '../../data/burgerKing';

/**
 * The first answer to "show me Burger King" is not 16 dishes — it is the shape
 * of the menu. Departments come back as cards so the shopper picks a section
 * the same way they pick anything else in Talab, and the dishes that follow
 * arrive as the normal result cards. One pattern, two steps.
 */
export function BrandCategoryCards({
  store,
  onPick
}: {
  store: BrandStore;
  onPick: (category: string) => void;
}) {
  const itemsIn = (c: string) => store.items.filter(i => i.category === c);
  return <div className="w-full" data-id="element-2078" data-mp="3k8vcedlwsd7b">
      {/* Who you're ordering from — one quiet line, no cover image */}
      <div className="flex items-center gap-3 mb-4" data-id="element-2079" data-mp="2now5l3r5sx3b">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center font-display font-bold text-[15px] text-white flex-shrink-0" style={{
        backgroundColor: store.colour
      }} data-id="element-2080" data-mp="2rrj05cf7sw47">
          {store.logoLetter}
        </span>
        <div className="min-w-0" data-id="element-2081" data-mp="4wt3g4q1zu22">
          <h3 className="font-display font-bold text-[17px] text-dark-navy leading-tight inline-flex items-center gap-1.5" data-id="element-2082" data-mp="3q7xr1puaj8">
            {store.name}
            <BadgeCheckIcon className="w-4 h-4 text-primary-indigo flex-shrink-0" data-id="element-2083" data-mp="3r9rkk41bxc6z" />
          </h3>
          <p className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12px] font-semibold text-slate-500 mt-1" data-id="element-2084" data-mp="3mcrf9vcb0dgx">
            <span className="inline-flex items-center gap-1" data-id="element-2085" data-mp="3ibpf9cakbxdw">
              <StarIcon className="w-3.5 h-3.5 fill-warning text-warning" data-id="element-2086" data-mp="3dbb4hmhku9c6" />
              {store.rating}
            </span>
            <span className="inline-flex items-center gap-1" data-id="element-2087" data-mp="38d9kmzcbu5zn">
              <ClockIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2088" data-mp="1bjere8frgrfa" />
              {store.deliveryTime}
            </span>
            <span className="inline-flex items-center gap-1" data-id="element-2089" data-mp="16ld93pq5sw4v">
              <BikeIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-2090" data-mp="30zqvysm5msvm" />
              {store.deliveryFee}
            </span>
            <span className="text-success" data-id="element-2091" data-mp="35s276rmci13h">{store.hours}</span>
          </p>
        </div>
      </div>

      {/* Ten departments are a menu index, not ten products — so each one is a
          compact tile with a thumbnail, not a full photo card. Five across
          puts the whole menu on one screen instead of three scrolls. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-id="element-2092" data-mp="3jrjnsjphm81r">
        {store.categories.map(c => {
        const items = itemsIn(c);
        if (items.length === 0) return null;
        return <motion.button key={c} onClick={() => onPick(c)} whileHover={{
          y: -2
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="transform-gpu text-left bg-white rounded-xl border border-slate-200 p-1.5 pr-2.5 flex items-center gap-2.5 shadow-soft hover:shadow-medium hover:border-primary-indigo/30 transition-[box-shadow,border-color] duration-200 ease-out group" data-id="element-2093" data-mp="39uyh7txtkovt">
              <span className="w-11 h-11 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0" data-id="element-2094" data-mp="uek07mkbdpo4">
                <img src={items[0].images[0]} alt="" className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.08]" data-id="element-2095" data-mp="qf1g2t0bhimc" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-2096" data-mp="3tsvu006qg17i">
                <span className="block font-display font-bold text-[13px] text-dark-navy leading-tight truncate" data-id="element-2097" data-mp="bhr31f8104oi">
                  {c}
                </span>
                <span className="block text-[11px] font-semibold text-slate-400 mt-0.5" data-id="element-2098" data-mp="3f5sr1dzn50cs">
                  {items.length} {items.length === 1 ? 'item' : 'items'}
                </span>
              </span>
              <ArrowRightIcon className="w-3.5 h-3.5 text-slate-300 group-hover:text-primary-indigo transition-colors duration-150 ease-out flex-shrink-0" data-id="element-2099" data-mp="3ntq4levad32y" />
            </motion.button>;
      })}
      </div>

      <p className="text-[12.5px] text-slate-400 mt-3" data-id="element-2100" data-mp="1lyj91ea0hvj">
        Or just type what you want — “chicken”, “breakfast”, “something under
        AED 20”.
      </p>
    </div>;
}